import "../../datagrid.scss";
import "./RutasDatatable.scss";
import { useContext, useEffect, useState, useRef, useMemo, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { VisitasContext } from "../../../context/visitas/VisitasContext";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import "dayjs/locale/es";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import MapIcon from "@mui/icons-material/Map";
import ListAltIcon from "@mui/icons-material/ListAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RouteIcon from "@mui/icons-material/Route";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import useDocumentTitle from "../../../components/Title";
import CustomDataGrid from "../../../components/DataGrid/CustomDataGrid";
import { fetchEntityRoutesData } from "./configGrid/apiCalls";
import { useFilters } from "../../../context/FiltersContext";

dayjs.locale("es");

// ─── Fix iconos Leaflet ───────────────────────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// ─── Iconos personalizados ────────────────────────────────────────────────────
const createVisitaIcon = (color, number, resaltado = false) => {
  const size = resaltado ? 42 : 28;
  const fontSize = resaltado ? 15 : 12;
  const border = resaltado ? "4px solid #FFD700" : "3px solid white";
  const shadow = resaltado
    ? "0 0 0 4px rgba(255,215,0,0.5), 0 3px 10px rgba(0,0,0,0.5)"
    : "0 2px 5px rgba(0,0,0,0.3)";

  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background:${color};color:#fff;width:${size}px;height:${size}px;
      border-radius:50%;display:flex;align-items:center;justify-content:center;
      font-weight:bold;font-size:${fontSize}px;border:${border};box-shadow:${shadow};
    ">${number}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2)],
  });
};

const createClienteIcon = (tienePedido, number, resaltado = false) => {
  const bgColor = tienePedido ? "#4CAF50" : "#FF5722";
  const emoji = tienePedido ? "🛒" : "🏠";
  const border = resaltado ? "3px solid #FFD700" : "2px solid white";
  const shadow = resaltado
    ? "0 0 0 4px rgba(255,215,0,0.5), 0 4px 12px rgba(0,0,0,0.5)"
    : "0 3px 8px rgba(0,0,0,0.4)";
  const w = resaltado ? 44 : 36;
  const h = resaltado ? 50 : 42;

  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background:${bgColor};color:#fff;width:${w}px;height:${h}px;
      display:flex;flex-direction:column;align-items:center;justify-content:flex-start;
      padding-top:2px;font-weight:bold;border:${border};border-radius:8px;
      box-shadow:${shadow};position:relative;
    ">
      <span style="font-size:${resaltado ? 14 : 12}px;font-weight:bold;line-height:1;">${number}</span>
      <span style="font-size:${resaltado ? 17 : 14}px;line-height:1;">${emoji}</span>
      <div style="position:absolute;bottom:-10px;left:50%;transform:translateX(-50%);
        width:0;height:0;border-left:10px solid transparent;border-right:10px solid transparent;
        border-top:10px solid ${bgColor};"></div>
    </div>`,
    iconSize: [w, h + 10],
    iconAnchor: [w / 2, h + 10],
    popupAnchor: [0, -(h + 10)],
  });
};

// ─── Componente para mover el mapa ────────────────────────────────────────────
const ChangeMapView = ({ center, zoom, flyTo = false }) => {
  const map = useMap();
  useEffect(() => {
    if (center?.[0] && center?.[1]) {
      flyTo
        ? map.flyTo(center, zoom, { animate: true, duration: 0.8 })
        : map.setView(center, zoom);
    }
  }, [center, zoom, map, flyTo]);
  return null;
};

// ─── Estilo del modal MUI ─────────────────────────────────────────────────────
const modalStyle = {
  position: "absolute",
  top: "50%", left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%", maxWidth: "1300px", maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

// ─── Hook isMobile ────────────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return isMobile;
};

// ─── Helpers de dirección ─────────────────────────────────────────────────────
const formatDireccion = (dir, nro, piso, dpto) => {
  if (!dir) return null;
  let s = [dir, nro].filter(Boolean).join(" ");
  if (piso || dpto) s += ` – Piso ${piso || ""}${dpto ? ` Dpto ${dpto}` : ""}`.trim();
  return s;
};

const formatDireccionEntrega = (v) => {
  if (!v.cli_direccion_entrega) return null;
  let s = [v.cli_direccion_entrega, v.cli_nro_calle_entrega].filter(Boolean).join(" ");
  if (v.cli_piso_entrega || v.cli_dpto_entrega)
    s += ` – Piso ${v.cli_piso_entrega || ""}${v.cli_dpto_entrega ? ` Dpto ${v.cli_dpto_entrega}` : ""}`.trim();
  return s;
};

// ─── Componente principal ─────────────────────────────────────────────────────
export const Datatable = () => {
  const titleGrid = "Mapa de Rutas Preventistas";
  useDocumentTitle(titleGrid);

  const { updateFilters, getFilters } = useFilters();
  const { getRutasPanelData } = useContext(VisitasContext);
  const isMobile = useIsMobile();

  // ── Estado general ─────────────────────────────────────────────────────────
  const [listaItems, setListaItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedHourFrom, setSelectedHourFrom] = useState(dayjs().hour(6).minute(0));
  const [selectedHourUntil, setSelectedHourUntil] = useState(dayjs().hour(23).minute(59));

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVendedor, setSelectedVendedor] = useState(null);
  const [visitasVendedor, setVisitasVendedor] = useState([]);
  const [modalView, setModalView] = useState("lista");

  const [mapCenter, setMapCenter] = useState([-27.7833, -64.2667]);
  const [mapZoom, setMapZoom] = useState(13);
  const [flyToMap, setFlyToMap] = useState(false);
  const [visitaSeleccionadaId, setVisitaSeleccionadaId] = useState(null);
  const [visitaFiltradaId, setVisitaFiltradaId] = useState(null);

  // ── Estado buscador/filtro (subidos al componente para respetar reglas de hooks) ──
  const [busqueda, setBusqueda] = useState("");
  const [filtroPedido, setFiltroPedido] = useState("todos"); // "todos" | "con" | "sin"

  const isFetchingRef = useRef(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => { fetchData(); }, [selectedDate, selectedHourFrom, selectedHourUntil]);

  const fetchData = async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const { dataEntity } = await fetchEntityRoutesData(
        getRutasPanelData,
        "",
        selectedDate?.format("YYYY-MM-DD") ?? "",
        selectedHourFrom?.format("HH:mm") ?? "00:00",
        selectedHourUntil?.format("HH:mm") ?? "23:59"
      );
      setListaItems(dataEntity || []);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  // ── Agrupación vendedores ──────────────────────────────────────────────────
  const vendedoresAgrupados = useMemo(() => {
    const grouped = listaItems.reduce((acc, item) => {
      const key = item.sel_codigo;
      if (!key) return acc;
      if (!acc[key]) {
        acc[key] = {
          id: key,
          sel_codigo: item.sel_codigo,
          sel_name: item.sel_name || "Sin nombre",
          total_visitas: 0,
          visitas_con_ubicacion: 0,
          visitas_con_pedido: 0,
          visitas: [],
        };
      }
      acc[key].total_visitas += 1;
      acc[key].visitas.push(item);
      if (item.vis_latitud && parseFloat(item.vis_latitud) !== 0)
        acc[key].visitas_con_ubicacion += 1;
      if (item.cli_estado_pedido === 0)
        acc[key].visitas_con_pedido += 1;
      return acc;
    }, {});

    return Object.values(grouped).map((item, i) => ({ ...item, index: i + 1 }));
  }, [listaItems]);

  const totalPedidos = useMemo(
    () => listaItems.filter((v) => v.cli_estado_pedido === 0).length,
    [listaItems]
  );

  // ── Datos mapa ─────────────────────────────────────────────────────────────
  const visitasOrdenadas = useMemo(
    () => [...visitasVendedor].sort((a, b) => (a.vis_hora || "").localeCompare(b.vis_hora || "")),
    [visitasVendedor]
  );

  const visitasFiltradas = useMemo(() => {
    return visitasOrdenadas.filter((v) => {
      const texto = busqueda.toLowerCase();
      const coincideTexto =
        !busqueda ||
        (v.razon_social || "").toLowerCase().includes(texto) ||
        (v.cli_name || "").toLowerCase().includes(texto) ||
        (v.cli_codigo || "").toLowerCase().includes(texto);

      const tienePedido = v.cli_estado_pedido === 0;
      const coincideFiltro =
        filtroPedido === "todos" ||
        (filtroPedido === "con" && tienePedido) ||
        (filtroPedido === "sin" && !tienePedido);

      return coincideTexto && coincideFiltro;
    });
  }, [visitasOrdenadas, busqueda, filtroPedido]);

  const visitasConCoordenadas = useMemo(
    () => visitasOrdenadas.filter((v) => v.vis_latitud && parseFloat(v.vis_latitud) !== 0),
    [visitasOrdenadas]
  );

  const visitasMostradas = useMemo(
    () => visitaFiltradaId
      ? visitasConCoordenadas.filter((v) => v.vis_id === visitaFiltradaId)
      : visitasConCoordenadas,
    [visitasConCoordenadas, visitaFiltradaId]
  );

  const rutaCoordenadas = useMemo(
    () => visitasConCoordenadas.map((v) => [parseFloat(v.vis_latitud), parseFloat(v.vis_longitud)]),
    [visitasConCoordenadas]
  );

  const getMarkerColor = (index, total) =>
    index === 0 ? "#4CAF50" : index === total - 1 ? "#f44336" : "#2196F3";

  const tieneCoordenadasCliente = (v) =>
    v.cli_latitud && parseFloat(v.cli_latitud) !== 0;

  // ── Handlers modal ─────────────────────────────────────────────────────────
  const handleVerVisitas = (vendedor) => {
    setSelectedVendedor(vendedor);
    setVisitasVendedor(vendedor.visitas || []);
    setModalView("lista");
    setModalOpen(true);
    setVisitaSeleccionadaId(null);
    setVisitaFiltradaId(null);
    setFlyToMap(false);
    // Resetear buscador y filtro al abrir
    setBusqueda("");
    setFiltroPedido("todos");
    const primera = (vendedor.visitas || []).find(
      (v) => v.vis_latitud && parseFloat(v.vis_latitud) !== 0
    );
    if (primera) {
      setMapCenter([parseFloat(primera.vis_latitud), parseFloat(primera.vis_longitud)]);
      setMapZoom(15);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedVendedor(null);
    setVisitasVendedor([]);
    setModalView("lista");
    setVisitaSeleccionadaId(null);
    setVisitaFiltradaId(null);
    setFlyToMap(false);
    setBusqueda("");
    setFiltroPedido("todos");
  };

  const handleVerVisita = useCallback((visita) => {
    setVisitaSeleccionadaId(visita.vis_id);
    setVisitaFiltradaId(null);
    setMapCenter([parseFloat(visita.vis_latitud), parseFloat(visita.vis_longitud)]);
    setMapZoom(17);
    setFlyToMap(true);
    setModalView("mapa");
  }, []);

  const handleVerSoloEsta = useCallback((visita) => {
    setVisitaSeleccionadaId(visita.vis_id);
    setVisitaFiltradaId(visita.vis_id);
    setMapCenter([parseFloat(visita.vis_latitud), parseFloat(visita.vis_longitud)]);
    setMapZoom(17);
    setFlyToMap(true);
    setModalView("mapa");
  }, []);

  const handleLimpiarFiltro = useCallback(() => {
    setVisitaFiltradaId(null);
    setVisitaSeleccionadaId(null);
    setFlyToMap(false);
  }, []);

  // ── Columnas ───────────────────────────────────────────────────────────────
  const columnsVendedores = [
    { field: "index", headerName: "#", width: 60, sortable: false },
    { field: "sel_codigo", headerName: "Código", width: 90 },
    { field: "sel_name", headerName: "Vendedor / Preventista", flex: 1, minWidth: 180 },
    {
      field: "total_visitas",
      headerName: "Visitas",
      width: 85,
      renderCell: ({ value }) => <span className="badge-visitas">{value}</span>,
    },
    {
      field: "visitas_con_pedido",
      headerName: "Pedidos",
      width: 90,
      renderCell: ({ value }) => (
        <span className={`badge-pedido ${value > 0 ? "sin-pedido" : "con-pedido"}`}>
          🛒 {value}
        </span>
      ),
    },
    {
      field: "visitas_con_ubicacion",
      headerName: "GPS",
      width: 75,
      renderCell: ({ value }) => (
        <span className={`badge-gps ${value > 0 ? "con-gps" : "sin-gps"}`}>
          📍 {value}
        </span>
      ),
    },
    {
      field: "acciones",
      headerName: "Acciones",
      width: 140,
      sortable: false,
      renderCell: ({ row }) => (
        <div className="acciones-cell">
          <button
            className="btn-ver-visitas"
            onClick={(e) => { e.stopPropagation(); handleVerVisitas(row); }}
          >
            <VisibilityIcon fontSize="small" />
            Ver Ruta
          </button>
        </div>
      ),
    },
  ];

  // ── Contenido modal ────────────────────────────────────────────────────────
  const renderModalContent = () => {
    console.log("filtro", filtroPedido)
    if (modalView === "lista") {
      return (
        <div className="modal-lista-visitas">
          <div className="lista-header">

            {/* Stats */}
            <div className="lista-stats">
              {(filtroPedido === "todos") &&
                <span className="lista-count">
                  <strong>{visitasFiltradas.length}</strong>
                  {visitasFiltradas.length !== visitasOrdenadas.length
                    ? ` de ${visitasOrdenadas.length}`
                    : ""}{" "}
                  visitas
                </span>
              }
              {(filtroPedido === "todos" || filtroPedido === "con" ) &&
                <span className="lista-pedidos con-pedido">
                  🛒 <strong>{visitasFiltradas.filter((v) => v.cli_estado_pedido === 0).length}</strong> con pedido
                </span>
              }
              {(filtroPedido === "todos" || filtroPedido === "sin" ) &&
                <span className="lista-pedidos sin-pedido-stat">
                  ❌ <strong>{visitasFiltradas.filter((v) => v.cli_estado_pedido !== 0).length}</strong> sin pedido
                </span>
              }
            </div>

            {/* Buscador + filtros */}
            <div className="lista-search-bar">
              <div className="search-input-wrapper">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Buscar por razón social, código, fantasía..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
                {busqueda && (
                  <button className="search-clear" onClick={() => setBusqueda("")}>✕</button>
                )}
              </div>
              <div className="filtro-pedido-btns">
                {[
                  { key: "todos", label: "Todos" },
                  { key: "con", label: "🛒 Con pedido" },
                  { key: "sin", label: "❌ Sin pedido" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    className={`btn-filtro ${filtroPedido === key ? "activo" : ""}`}
                    onClick={() => setFiltroPedido(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Botón ver mapa */}
            {visitasConCoordenadas.length > 0 && (
              <button className="btn-ver-mapa" onClick={() => setModalView("mapa")}>
                <MapIcon fontSize="small" />
                Ver Mapa ({visitasConCoordenadas.length} con GPS)
              </button>
            )}
          </div>

          <div className="lista-visitas-container">
            {!isMobile ? (
              <table className="tabla-visitas">
                <thead>
                  <tr>
                    <th>#</th><th>Hora</th><th>Código</th>
                    <th>Razón Social</th><th>Nombre de Fantasía</th>
                    <th>Dirección</th><th>Pedido</th><th>Observación</th><th>GPS</th>
                  </tr>
                </thead>
                <tbody>
                  {visitasFiltradas.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: "24px", color: "#888" }}>
                        No se encontraron visitas con ese criterio
                      </td>
                    </tr>
                  ) : (
                    visitasFiltradas.map((v, i) => {
                      const pedido = v.cli_estado_pedido === 0;
                      return (
                        <tr key={v.vis_id || i} className={pedido ? "row-con-pedido" : "row-sin-pedido"}>
                          <td className="td-index">{i + 1}</td>
                          <td className="td-hora">{v.vis_hora || "N/A"}</td>
                          <td className="td-codigo">{v.cli_codigo || "N/A"}</td>
                          <td className="td-cliente">{v.razon_social || "Sin Razón Social"}</td>
                          <td className="td-cliente">{v.cli_name || ""}</td>
                          <td className="td-direccion">
                            {formatDireccion(v.cliente.cli_direccion, v.cliente.cli_numero, v.cliente.cli_piso, v.cliente.cli_dpto)}
                          </td>
                          <td className="td-pedido">
                            {pedido
                              ? <span className="pedido-si">✓ Sí</span>
                              : <span className="pedido-no">✗ No</span>}
                          </td>
                          <td className="td-observacion">{v.vis_observacion || "-"}</td>
                          <td className="td-gps">
                            {v.vis_latitud && parseFloat(v.vis_latitud) !== 0 ? (
                              <button
                                className="btn-ir-mapa"
                                onClick={() => window.open(`https://www.google.com/maps?q=${v.cli_latitud},${v.cli_longitud}`, "_blank")}
                              >📍 Ir</button>
                            ) : (
                              <span className="gps-no">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            ) : (
              <div className="cards-visitas">
                {visitasFiltradas.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "24px", color: "#888" }}>
                    No se encontraron visitas con ese criterio
                  </div>
                ) : (
                  visitasFiltradas.map((v, i) => {
                    const pedido = v.cli_estado_pedido === 0;
                    return (
                      <div key={v.vis_id || i} className={`card-visita ${pedido ? "con-pedido" : "sin-pedido"}`}>
                        <div className="card-header">
                          <span>#{i + 1}</span>
                          <span>{v.vis_hora || "N/A"}</span>
                        </div>
                        <div className="card-body">
                          <p><strong>Razón Social:</strong> {v.razon_social}</p>
                          <p><strong>Fantasía:</strong> {v.cli_name}</p>
                          <p><strong>Código:</strong> {v.cli_codigo}</p>
                          <p><strong>Dirección:</strong> {formatDireccion(v.cliente.cli_direccion, v.cliente.cli_numero, v.cliente.cli_piso, v.cliente.cli_dpto)}</p>
                          <p><strong>Pedido:</strong> {pedido ? "✓ Sí" : "✗ No"}</p>
                          <p><strong>Obs:</strong> {v.vis_observacion || "-"}</p>
                        </div>
                        <button
                          className="btn-ir-mapa"
                          onClick={() => window.open(`https://www.google.com/maps?q=${v.cli_latitud},${v.cli_longitud}`, "_blank")}
                        >📍 Ir a la ubicación</button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    // ── Vista mapa ────────────────────────────────────────────────────────────
    return (
      <div className="modal-mapa-container">
        <div className="mapa-header-actions">
          <button className="btn-volver-lista" onClick={() => setModalView("lista")}>
            <ListAltIcon fontSize="small" /> Volver a Lista
          </button>
          <div className="mapa-legend-modal">
            {isMobile ? (
              <div className="legend-mobile">
                {[["#4CAF50", "Inicio"], ["#2196F3", "Visita"], ["#f44336", "Fin"]].map(([c, l]) => (
                  <div key={l} className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: c }} />
                    <span>{l}</span>
                  </div>
                ))}
                <div className="legend-item">
                  <span className="legend-house con-pedido">🛒</span>
                  <span>Con Pedido</span>
                </div>
                <div className="legend-item">
                  <span className="legend-house sin-pedido">🏠</span>
                  <span>Sin Pedido</span>
                </div>
              </div>
            ) : (
              <>
                <div className="legend-section">
                  <span className="legend-title">Ruta:</span>
                  {[["#4CAF50", "Inicio"], ["#2196F3", "Visita"], ["#f44336", "Fin"]].map(([c, l]) => (
                    <div key={l} className="legend-item">
                      <span className="legend-dot" style={{ backgroundColor: c }} />
                      <span>{l}</span>
                    </div>
                  ))}
                </div>
                <div className="legend-divider" />
                <div className="legend-section">
                  <span className="legend-title">Cliente:</span>
                  <div className="legend-item">
                    <span className="legend-house con-pedido">🛒</span>
                    <span>Con Pedido</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-house sin-pedido">🏠</span>
                    <span>Sin Pedido</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mapa-content-modal">
          <div className="mapa-wrapper-modal">
            <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: "100%", width: "100%" }}>
              <ChangeMapView center={mapCenter} zoom={mapZoom} flyTo={flyToMap} />
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {!visitaFiltradaId && rutaCoordenadas.length > 1 && (
                <Polyline positions={rutaCoordenadas} color="#1976d2" weight={4} opacity={0.8} dashArray="10,10" />
              )}

              {visitasMostradas.map((v, i) => {
                if (!tieneCoordenadasCliente(v)) return null;
                const pedido = v.cli_estado_pedido === 0;
                const idx = visitasConCoordenadas.findIndex((x) => x.vis_id === v.vis_id);
                const resaltado = visitaSeleccionadaId === v.vis_id;
                return (
                  <Marker
                    key={`c-${v.vis_id || i}`}
                    position={[parseFloat(v.cli_latitud), parseFloat(v.cli_longitud)]}
                    icon={createClienteIcon(pedido, idx + 1, resaltado)}
                    zIndexOffset={resaltado ? 500 : 100}
                  >
                    <Popup>
                      <div className="popup-content">
                        <h4 className={pedido ? "con-pedido" : "sin-pedido"}>
                          {pedido ? "🛒" : "🏠"} Cliente #{idx + 1}
                        </h4>
                        <p><strong>👤</strong> {v.razon_social}</p>
                        <p><strong>🏟</strong> {v.cli_name || "N/A"}</p>
                        <p><strong>🔖</strong> {v.cli_codigo || "N/A"}</p>
                        {formatDireccion(v.cliente.cli_direccion, v.cliente.cli_numero, v.cliente.cli_piso, v.cliente.cli_dpto) && (
                          <p>🏠 <strong>Dir:</strong> {formatDireccion(v.cliente.cli_direccion, v.cliente.cli_numero, v.cliente.cli_piso, v.cliente.cli_dpto)}</p>
                        )}
                        {formatDireccionEntrega(v.cliente) && (
                          <p>🚚 <strong>Entrega:</strong> {formatDireccionEntrega(v.cliente)}</p>
                        )}
                        <p><strong>📦 Pedido:</strong> {pedido
                          ? <span className="estado-pedido si">✓ REALIZADO</span>
                          : <span className="estado-pedido no">✗ NO REALIZÓ</span>}
                        </p>
                        <button
                          className="btn-ir-mapa"
                          onClick={() => window.open(`https://www.google.com/maps?q=${v.cli_latitud},${v.cli_longitud}`, "_blank")}
                        >📍 Ir a la ubicación</button>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              {visitasMostradas.map((v, i) => {
                const idx = visitasConCoordenadas.findIndex((x) => x.vis_id === v.vis_id);
                const resaltado = visitaSeleccionadaId === v.vis_id;
                return (
                  <Marker
                    key={`v-${v.vis_id || i}`}
                    position={[parseFloat(v.vis_latitud), parseFloat(v.vis_longitud)]}
                    icon={createVisitaIcon(getMarkerColor(idx, visitasConCoordenadas.length), idx + 1, resaltado)}
                    zIndexOffset={resaltado ? 2000 : 1000}
                  >
                    <Popup>
                      <div className="popup-content">
                        <h4>📍 Visita #{idx + 1}</h4>
                        <p><strong>🕐 Hora:</strong> {v.vis_hora || "N/A"}</p>
                        <p><strong>🔖 Código:</strong> {v.cli_codigo || "N/A"}</p>
                        <p><strong>👤</strong> {v.razon_social}</p>
                        {formatDireccion(v.cliente.cli_direccion, v.cliente.cli_numero, v.cliente.cli_piso, v.cliente.cli_dpto) && (
                          <p>🏠 <strong>Dir:</strong> {formatDireccion(v.cliente.cli_direccion, v.cliente.cli_numero, v.cliente.cli_piso, v.cliente.cli_dpto)}</p>
                        )}
                        {formatDireccionEntrega(v.cliente) && (
                          <p>🚚 <strong>Entrega:</strong> {formatDireccionEntrega(v.cliente)}</p>
                        )}
                        <p><strong>📦 Pedido:</strong> {v.cli_estado_pedido === 0
                          ? <span className="estado-pedido si">✓ SÍ</span>
                          : <span className="estado-pedido no">✗ NO</span>}
                        </p>
                        {v.vis_observacion && <p><strong>📝</strong> {v.vis_observacion}</p>}
                        <p className="popup-tipo">🚶 Ubicación de la visita</p>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>

          <div className="mapa-sidebar-modal">
            <h4>📋 Recorrido</h4>
            {visitaFiltradaId && (
              <button className="btn-limpiar-filtro" onClick={handleLimpiarFiltro}>
                🔄 Mostrar todas
              </button>
            )}
            <div className="sidebar-list">
              {visitasConCoordenadas.map((v, i) => {
                const pedido = v.cli_estado_pedido === 0;
                const isSelec = visitaSeleccionadaId === v.vis_id;
                const isOculta = visitaFiltradaId && visitaFiltradaId !== v.vis_id;
                const classes = [
                  "sidebar-item",
                  i === 0 ? "inicio" : "",
                  i === visitasConCoordenadas.length - 1 ? "fin" : "",
                  isSelec ? "seleccionada" : "",
                  isOculta ? "oculta" : "",
                ].filter(Boolean).join(" ");

                return (
                  <div key={v.vis_id || i} className={classes}>
                    <div className="item-number" style={{ backgroundColor: getMarkerColor(i, visitasConCoordenadas.length) }}>
                      {i + 1}
                    </div>
                    <div className="item-info">
                      <span className="item-hora">{v.vis_hora}</span>
                      <span className="item-cliente">{v.razon_social}</span>
                      <span className="item-cliente">{v.cli_name}</span>
                      {formatDireccion(v.cliente.cli_direccion, v.cliente.cli_numero, v.cliente.cli_piso, v.cliente.cli_dpto) && (
                        <span className="item-direccion">
                          🏠 {formatDireccion(v.cliente.cli_direccion, v.cliente.cli_numero, v.cliente.cli_piso, v.cliente.cli_dpto)}
                        </span>
                      )}
                      {formatDireccionEntrega(v.cliente) && (
                        <span className="item-direccion item-direccion--entrega">
                          🚚 {formatDireccionEntrega(v.cliente)}
                        </span>
                      )}
                      <span className={`item-pedido ${pedido ? "si" : "no"}`}>
                        {pedido ? "🛒 Pedido" : "❌ Sin pedido"}
                      </span>
                      <div className="item-actions">
                        <button className="btn-accion-ver" onClick={() => handleVerVisita(v)}>🔍 Ver</button>
                        <button className="btn-accion-solo" onClick={() => handleVerSoloEsta(v)}>📌 Solo esta</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <div className="datatable rutas-datatable">

        {/* Título */}
        <div className="datatableTitle">
          <span>{titleGrid}</span>
        </div>

        {/* Filtros */}
        <div className="filtros-container">
          <div className="filtro-item fecha">
            <DatePicker
              label="Fecha"
              value={selectedDate}
              onChange={setSelectedDate}
              inputFormat="DD/MM/YYYY"
              renderInput={(params) => <TextField {...params} size="small" />}
            />
          </div>
          <div className="filtro-item hora">
            <TimePicker
              label="Desde"
              value={selectedHourFrom}
              onChange={setSelectedHourFrom}
              ampm={false}
              renderInput={(params) => <TextField {...params} size="small" />}
            />
          </div>
          <div className="filtro-item hora">
            <TimePicker
              label="Hasta"
              value={selectedHourUntil}
              onChange={setSelectedHourUntil}
              ampm={false}
              renderInput={(params) => <TextField {...params} size="small" />}
            />
          </div>
        </div>

        {/* Resumen */}
        <div className="resumen-vendedores">
          <div className="resumen-item fecha">
            <CalendarMonthIcon />
            <span>
              {selectedDate?.format("dddd, DD [de] MMMM YYYY") ?? "Sin fecha"}
              {" | "}
              {selectedHourFrom?.format("HH:mm")} – {selectedHourUntil?.format("HH:mm")}
            </span>
          </div>
          <div className="resumen-item">
            <RouteIcon />
            <span><strong>{vendedoresAgrupados.length}</strong> Vendedores</span>
          </div>
          <div className="resumen-item">
            <VisibilityIcon />
            <span><strong>{listaItems.length}</strong> Visitas</span>
          </div>
          <div className="resumen-item pedidos">
            <ShoppingCartIcon />
            <span><strong>{totalPedidos}</strong> Pedidos</span>
          </div>
        </div>

        {/* Tabla */}
        {isLoading ? (
          <div className="empty-state-tabla">
            <CircularProgress size={40} />
            <p>Cargando datos...</p>
          </div>
        ) : vendedoresAgrupados.length === 0 ? (
          <div className="empty-state-tabla">
            <CalendarMonthIcon sx={{ fontSize: 56 }} />
            <p>No hay visitas para esta fecha y horario</p>
            <span>Seleccioná otra fecha u horario para ver los recorridos</span>
          </div>
        ) : (
          <div className="datagridWrapper">
            <CustomDataGrid
              rows={vendedoresAgrupados}
              columns={columnsVendedores}
              rowCount={vendedoresAgrupados.length}
              isLoading={isLoading}
              onRowClick={() => { }}
              enableSelectionClick={false}
            />
          </div>
        )}

        {/* Modal */}
        <Modal open={modalOpen} onClose={handleCloseModal}>
          <Box sx={modalStyle}>
            <div className="modal-header">
              <div className="modal-title">
                <h2>{selectedVendedor?.sel_name ?? "Vendedor"}</h2>
                <span className="modal-subtitle">
                  Código: {selectedVendedor?.sel_codigo} | {selectedDate?.format("DD/MM/YYYY")} | Visitas: {selectedVendedor?.total_visitas} | Pedidos: {selectedVendedor?.visitas_con_pedido}
                </span>
              </div>
              <IconButton onClick={handleCloseModal} className="modal-close-btn">
                <CloseIcon />
              </IconButton>
            </div>
            <div className="modal-body">{renderModalContent()}</div>
          </Box>
        </Modal>

      </div>
    </LocalizationProvider>
  );
};

export default Datatable;